/**
 * useRollbackDeployment Hook
 *
 * React Query mutation for rolling back a deployment.
 * Note: Cannot rollback the currently active deployment.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { deploymentService } from '../../../api/deployment.service';
import type { DeploymentSanitized } from '../../../types/deployment.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseRollbackDeploymentOptions {
  tenantId: string;
  applicationId: string;
  onSuccess?: (data: DeploymentSanitized) => void;
  onError?: (error: Error) => void;
}

interface RollbackDeploymentVariables {
  deploymentId: string;
  reason: string;
}

/**
 * Hook for rolling back a deployment
 */
export const useRollbackDeployment = (
  options: UseRollbackDeploymentOptions
): UseMutationResult<DeploymentSanitized, Error, RollbackDeploymentVariables> => {
  const { tenantId, applicationId, onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation<DeploymentSanitized, Error, RollbackDeploymentVariables>({
    mutationFn: async ({ deploymentId, reason }: RollbackDeploymentVariables) => {
      console.log('[useRollbackDeployment] Rolling back deployment:', deploymentId, 'Reason:', reason);
      return await deploymentService.rollbackDeployment(tenantId, deploymentId, { reason });
    },
    onSuccess: (data) => {
      console.log('[useRollbackDeployment] Deployment rolled back successfully:', data.deploymentId);
      // Invalidate deployments list query
      queryClient.invalidateQueries({ queryKey: ['deployments', tenantId, applicationId] });
      onSuccess?.(data);
    },
    onError: (error) => {
      console.error('[useRollbackDeployment] Failed to rollback deployment:', getErrorMessage(error));
      onError?.(error);
    },
  });
};
